/**
 * Money on the Billing tab (handover L7): always in the invoice's own currency
 * with a symbol that names it ("A$583.60", "NZ$12.00"), never a bare "$" and
 * never the org's trading currency.
 *
 * Not `useCurrencyFormatter().format`: it uses `currencyDisplay:
 * 'narrowSymbol'`, which prints AUD as a bare "$" (the BillingUsagePanel bug
 * this tab replaces). `en-US` is deliberate in every language: it is the
 * locale whose symbol for AUD is "A$" (`en-AU` prints "$", `zh-CN` "AU$"). A
 * currency whose `en-US` symbol is still a bare "$" (USD) falls back to its
 * code ("USD 583.60"), so no amount on this tab is ever ambiguous.
 *
 * Amounts arrive in cents (Stripe's minor units, as the backend returns them).
 */

const FORMAT_LOCALE = 'en-US';

const cache = new Map<string, Intl.NumberFormat>();

function formatterFor(currency: string, maxFractionDigits: number): Intl.NumberFormat | null {
  const key = `${currency}|${maxFractionDigits}`;
  const hit = cache.get(key);
  if (hit) return hit;
  try {
    const symbol = new Intl.NumberFormat(FORMAT_LOCALE, { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;
    const formatter = new Intl.NumberFormat(FORMAT_LOCALE, {
      style: 'currency',
      currency,
      currencyDisplay: symbol === '$' ? 'code' : 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: maxFractionDigits,
    });
    cache.set(key, formatter);
    return formatter;
  } catch {
    // An unknown code: Intl throws a RangeError. Never crash a bill over it.
    return null;
  }
}

function normalise(currency: string): string {
  return currency.trim().toUpperCase();
}

/** "A$583.60" from 58360 cents. */
export function formatMoney(cents: number, currency: string): string {
  const code = normalise(currency);
  const formatter = formatterFor(code, 2);
  const value = cents / 100;
  return formatter ? formatter.format(value) : `${value.toFixed(2)} ${code}`;
}

/**
 * A unit price, which can be a fraction of a cent (Stripe's
 * `unit_amount_decimal`): "A$0.10", "A$0.005". Up to 4 decimals, never
 * rounded to 2, so a price never reads as something it is not.
 */
export function formatUnitPrice(cents: number, currency: string): string {
  const code = normalise(currency);
  const formatter = formatterFor(code, 4);
  const value = cents / 100;
  return formatter ? formatter.format(value) : `${value} ${code}`;
}

/** Stripe's `unit_amount_decimal` (cents, as a decimal string) as a number, or null. */
export function parseUnitCents(decimal: string | null): number | null {
  if (decimal === null || decimal.trim() === '') return null;
  const n = Number(decimal);
  return Number.isFinite(n) ? n : null;
}

const CARD_BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'eftpos',
};

/** Stripe's card brand id ("visa") as people write it ("Visa"). */
export function cardBrandLabel(brand: string): string {
  const known = CARD_BRANDS[brand.toLowerCase()];
  if (known) return known;
  return brand.length > 0 ? brand.charAt(0).toUpperCase() + brand.slice(1) : brand;
}
